import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDepartments, createDepartment, deleteDepartment, getOrganization, getErrorMessage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export const DepartmentsPage = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [departments, setDepartments] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [deptsRes, orgRes] = await Promise.all([
        getDepartments(),
        getOrganization()
      ]);
      setDepartments(deptsRes.data);
      setOrganization(orgRes.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    
    try {
      await createDepartment({ name: newDeptName, org_id: organization?.id });
      toast.success('Department created successfully');
      setDialogOpen(false);
      setNewDeptName('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create department'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      await deleteDepartment(id);
      toast.success('Department deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete department'));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="departments-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const maxDepts = organization?.max_departments || 10;
  const isUnlimited = maxDepts === -1;
  const canCreate = isUnlimited || departments.length < maxDepts;

  return (
    <div className="space-y-8" data-testid="departments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
            Departments
          </h1>
          <p className="text-muted-foreground mt-1">
            {departments.length}{isUnlimited ? '' : ` of ${maxDepts}`} departments{isUnlimited ? '' : ' used'}
          </p>
        </div>
        
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full"
                disabled={!canCreate}
                data-testid="create-department-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Department
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Create Department</DialogTitle>
                <DialogDescription>
                  Add a new department to your organization
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Department Name</Label>
                  <Input
                    placeholder="e.g., Engineering, Sales, HR"
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    data-testid="department-name-input"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-department-btn">
                  Create Department
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Departments Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.length === 0 ? (
          <Card className="col-span-full">
            <div className="empty-state py-12">
              <Building2 className="empty-state-icon" />
              <p className="text-muted-foreground">No departments yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first department to get started
              </p>
            </div>
          </Card>
        ) : (
          departments.map((dept) => (
            <Card key={dept.id} data-testid={`department-${dept.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{dept.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Created {format(parseISO(dept.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(dept.id)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`delete-department-${dept.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
